import { Input } from "antd"
import { Button } from "components"
import { IShiftSwapRequest } from "../interface/shiftData"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useState } from "react"
import { formatDateNumeric } from "../utils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"

const ShiftSwapRequestModal = ({
  open,
  onClose,
  swap,
}: {
  open: boolean
  onClose: () => void
  swap: IShiftSwapRequest | null
}) => {
  const [reason, setReason] = useState("")
  const receiving = swap?.sourceShiftAssignment
  const transferring = swap?.targetShiftAssignment
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()

  const { mutate: rejectSwap, isLoading: isLoadingReject } = useMutation({
    mutationFn: async (body: { shiftSwapIds: number[]; reason: string }) => {
      return await ewaAxiosInstance.post(
        `/v1/time-attendance/shift-swaps/reject`,
        body
      )
    },
    onSuccess: (data: any) => {
      const res = data?.data
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: ["/v1/time-attendance/shift-swaps/requests"],
        })
        queryClient.invalidateQueries({
          queryKey: [
            "/v1/time-attendance/shift-swaps/requests/requested-by-employee",
          ],
        })
        res.message && toast.success(res.message)
        onClose()
      }
    },
    onError: onQueryError,
  })

  const { mutate: approveSwap, isLoading: isLoadingApprove } = useMutation({
    mutationFn: async (body: { shiftSwapIds: number[]; reason: string }) => {
      return await ewaAxiosInstance.post(
        `/v1/time-attendance/shift-swaps/${swap?.id}/target-approve`,
        body
      )
    },
    onSuccess: (data: any) => {
      const res = data?.data
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: ["/v1/time-attendance/shift-swaps/requests"],
        })
        queryClient.invalidateQueries({
          queryKey: [
            "/v1/time-attendance/shift-swaps/requests/requested-by-employee",
          ],
        })
        res.message && toast.success(res.message)
        onClose()
      }
    },
    onError: onQueryError,
  })

  return (
    <>
      <CustomModal
        open={open}
        onCancel={onClose}
        title="Shift swap request"
      >
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-lg text-[#0F172B] font-bold mb-2">
              Swap request from {swap?.requester.fullName}
            </p>
            {swap?.targetSwapDate ? (
              <p className="text-sm text-[#0F172B] mb-2">
                This colleague wants to swap their{" "}
                <span className="font-bold">
                  {receiving?.shiftPeriod?.title}{" "}
                  <span className="font-normal">on </span>
                  {formatDateNumeric(swap?.sourceSwapDate ?? null)}
                </span>{" "}
                with{" "}
                <span className="font-bold">
                  your {transferring?.shiftPeriod?.title}{" "}
                  <span className="font-normal">on </span>
                  {formatDateNumeric(swap?.targetSwapDate ?? null)}
                </span>
              </p>
            ) : (
              <p className="text-sm text-[#0F172B] mb-2">
                This colleague wants to give you their{" "}
                <span className="font-bold">
                  {receiving?.shiftPeriod?.title}{" "}
                  <span className="font-normal">on </span>
                  {formatDateNumeric(swap?.sourceSwapDate ?? null)}
                </span>
              </p>
            )}

            <label className="text-sm font-medium text-[#0F172B] mb-1">
              Reason [optional]
            </label>
            <Input.TextArea
              value={reason}
              rows={3}
              maxLength={150}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reasons for swapping this shift"
              className="text-sm"
            />
            <p className="text-xs text-[#62748E] my-2">
              Note: You can't edit your reason after saving
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() =>
                rejectSwap({ shiftSwapIds: [swap!.id], reason: reason })
              }
              submitting={isLoadingReject}
              disabled={isLoadingReject || isLoadingApprove}
              color="neutral-outline"
              className="px-6 py-2 w-full"
              style={{ color: "black", fontWeight: "initial" }}
            >
              Reject Request
            </Button>
            <Button
              onClick={() => {
                if (!swap) return
                approveSwap({ shiftSwapIds: [swap.id], reason: reason })
              }}
              submitting={isLoadingApprove}
              disabled={isLoadingReject || isLoadingApprove}
              color="primary"
              className="px-6 py-2 w-full"
              style={{ fontWeight: "initial" }}
            >
              Accept Request
            </Button>
          </div>
        </div>
      </CustomModal>
    </>
  )
}

export default ShiftSwapRequestModal
